import Job, { Amendment } from 'jobs/Job'
import { TableSortOrder } from 'components/Table'
import { action, computed, observable, makeObservable } from 'mobx'
import orderBy from 'lodash/orderBy'

type AmendmentTableColumns = {
  key: keyof Pick<
    Amendment,
    | 'description'
    | 'visibility'
    | 'customer_amount'
    | 'provider_amount'
    | 'date_created'
  >
  name:
    | 'description'
    | 'visibility'
    | 'customer_amount'
    | 'provider_amount'
    | 'date_created'
  header: string
  sortable?: boolean
}
/**
 * List of columns in the amendments table.
 */
const amendmentTableColumns: AmendmentTableColumns[] = [
  {
    key: 'description',
    name: 'description',
    header: 'Description',
    sortable: true,
  },
  {
    key: 'visibility',
    name: 'visibility',
    header: 'Visibility',
    sortable: true,
  },
  {
    key: 'customer_amount',
    name: 'customer_amount',
    header: 'Client price',
    sortable: true,
  },
  {
    key: 'provider_amount',
    name: 'provider_amount',
    header: 'Pro cut',
    sortable: true,
  },
  {
    key: 'date_created',
    name: 'date_created',
    header: 'Date',
    sortable: true,
  },
]

export default class AmendmentsTableViewModel {
  /**
   * Job representation for fetching amendments.
   */
  @observable job: Job = Job.none

  /**
   * The default sort order when first listing the amendments in the table.
   */
  @observable sortOrder: TableSortOrder = 'desc'

  /**
   * The default sort field (column) when first listing the amendments in the table.
   */
  @observable sortField: AmendmentTableColumns['key'] = 'date_created'

  /**
   * `AmendmentsTableViewModel` constructor
   * @param job
   */
  constructor(job: Job) {
    makeObservable(this)

    this.job = job
  }

  /**
   * Gets the name of the columns in the table.
   */
  @computed
  get columns() {
    return amendmentTableColumns.filter((c) => c.name)
  }

  /**
   * Current column to sort.
   */
  @computed
  get currentSort() {
    return this.sortField
  }

  /**
   * Current sort order.
   */
  @computed
  get currentSortOrder() {
    return this.sortOrder
  }

  /**
   * Ordered amendments to display.
   */
  @computed
  get orderedAmendments() {
    return orderBy(
      this.job.amendments,
      [this.sortField],
      this.sortOrder === 'none' ? undefined : this.sortOrder
    )
  }

  /**
   * Set the sorting order in the specified field (column).
   * @param sortField Field (column) to sort.
   * @param sortOrder Sort order.
   */
  @action.bound
  setSort(sortField: AmendmentTableColumns['key'], sortOrder: TableSortOrder) {
    this.sortField = sortField
    this.sortOrder = sortOrder
  }
}
